import { Badge, EmptyState, icons } from "@bruntworkwear-apps/ui";
import { humanise } from "@/shared/lib";
import type { Severity } from "@/shared/api";
import styles from "./FlagList.module.css";
import type { FlagListProps } from "./FlagList.types";

/** Errors first, then warnings, then notes. */
const ORDER: Record<Severity, number> = { error: 0, warning: 1, info: 2 };

const TONE: Record<Severity, "danger" | "warning" | "success"> = {
  error: "danger",
  warning: "warning",
  // Green: an informational flag means the app handled it correctly and there is
  // nothing to change - the USA lines carrying no customs, or a receipt quantity
  // that matched the tracker exactly. Neutral read as "unclassified".
  info: "success",
};

/**
 * Everything the run could not resolve on its own.
 *
 * Each entry carries the numbers involved, because a flag that only says
 * something is wrong leaves the reviewer to redo the work by hand.
 */
export function FlagList({ flags }: FlagListProps) {
  if (flags.length === 0) {
    return (
      <EmptyState
        icon={<icons.CheckCircle2 size={28} />}
        title="Nothing flagged"
        description="Every purchase order matched cleanly and every rate resolved."
      />
    );
  }

  const ordered = [...flags].sort(
    (a, b) => ORDER[a.severity] - ORDER[b.severity] || (a.po ?? "").localeCompare(b.po ?? ""),
  );

  return (
    <div className={styles.list}>
      {ordered.map((flag, index) => (
        <div
          key={`${flag.code}-${flag.po ?? ""}-${flag.row_number ?? index}`}
          className={[styles.item, styles[flag.severity]].filter(Boolean).join(" ")}
        >
          <div className={styles.body}>
            <span className={styles.heading}>
              <Badge tone={TONE[flag.severity]}>{humanise(flag.code)}</Badge>
              {flag.po && <span>PO {flag.po}</span>}
              {flag.document_number && <span>{flag.document_number}</span>}
              {flag.row_number !== null && <span>row {flag.row_number}</span>}
            </span>
            <span className={styles.detail}>{flag.detail}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
